import {
  Modal, Box, Typography, IconButton, CircularProgress, useMediaQuery, useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Close, tpTheme } from "@trainingpeaks/designtokens";
import { ZUORA_PUBLISHABLE_KEY } from "@config";

const baseModalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
};

// sx is required here to target the Zuora-rendered iframe — it's a
// third-party element with a fixed pixel width baked into Zuora's
// Payment Form config, and Tailwind cannot express a descendant
// selector on markup we don't control.
const zuoraContainerStyle = {
  "& iframe": {
    maxWidth: "100%",
    width: "100%",
  },
};

interface AddCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  error: string | null;
  zuoraContainerId: string;
}

const AddCardModal = ({ isOpen, onClose, isLoading, error, zuoraContainerId }: AddCardModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const modalStyle = { ...baseModalStyle, width: isNarrow ? "90%" : 500 };

  return (
    <Modal open={isOpen} onClose={onClose} data-testid="add-card-modal">
      <Box sx={modalStyle} className="p-4 sm:p-6">
        <Box className="flex justify-between items-center mb-4">
          <Typography variant="h6" className="font-bold">
            {t("Add Card")}
          </Typography>
          <IconButton onClick={onClose} aria-label={t("Close")}>
            <Close size="24" fill={tpTheme.semanticLayer.colors.disabled.main} />
          </IconButton>
        </Box>

        {isLoading && (
          <Box className="flex justify-center py-8">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" className="mb-4">
            {t(error)}
          </Typography>
        )}

        {!isLoading && !error && window.Zuora && ZUORA_PUBLISHABLE_KEY && (
          <Typography variant="caption" color="text.secondary" className="mb-3 block">
            {t(
              "A temporary $1.00 charge will be made to validate"
              + " your payment method."
              + " This charge is automatically refunded.",
            )}
          </Typography>
        )}

        <Box
          id={zuoraContainerId}
          sx={zuoraContainerStyle}
          className={`overflow-auto max-h-[75vh] ${isLoading ? "hidden" : ""}`}
        />

        {!isLoading && (!window.Zuora || !ZUORA_PUBLISHABLE_KEY) && !error && (
          <Typography color="error" variant="body2">
            {t("Unable to load payment form. Please try again later.")}
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default AddCardModal;
